import type { WSMessage, WSConnectionStatus, WSEventType } from '@/types';

type EventHandler = (payload: Record<string, unknown>) => void;

const WS_URL = '/v1/ws/dialogue';
const HEARTBEAT_INTERVAL = 30000;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000];
const MAX_RECONNECT_ATTEMPTS = 5;

class WebSocketManager {
  private ws: WebSocket | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private handlers = new Map<WSEventType, Set<EventHandler>>();
  private statusListeners = new Set<(status: WSConnectionStatus) => void>();
  private _status: WSConnectionStatus = 'disconnected';
  private lastMessageId: string | null = null;
  private sessionId: string | null = null;

  get status(): WSConnectionStatus {
    return this._status;
  }

  private setStatus(status: WSConnectionStatus) {
    this._status = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  connect(token: string) {
    // Skip if already connected or connecting
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    // Clean up any stale connection
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this.reconnectAttempts = 0;
    this.setStatus('connecting');

    const params = new URLSearchParams({ token });
    if (this.sessionId) params.set('session_id', this.sessionId);
    if (this.lastMessageId) params.set('last_message_id', this.lastMessageId);

    // Use relative URL - goes through Vite proxy (with ws:true) in dev, nginx in prod
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}${WS_URL}?${params}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.setStatus('connected');
      this.reconnectAttempts = 0;
      this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data as string);
        if (msg.message_id) this.lastMessageId = msg.message_id;
        const handlers = this.handlers.get(msg.type);
        if (handlers) {
          handlers.forEach((handler) => handler(msg.payload));
        }
      } catch {
        // ignore malformed messages
      }
    };

    this.ws.onclose = () => {
      this.stopHeartbeat();
      this.setStatus('disconnected');
      this.attemptReconnect(token);
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = MAX_RECONNECT_ATTEMPTS;
    this.ws?.close();
    this.ws = null;
    this.setStatus('disconnected');
  }

  send(message: Omit<WSMessage, 'timestamp'>) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ ...message, timestamp: Date.now() }));
  }

  on(event: WSEventType, handler: EventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
    return () => { this.handlers.get(event)?.delete(handler); };
  }

  onStatusChange(listener: (status: WSConnectionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => { this.statusListeners.delete(listener); };
  }

  setSessionId(id: string) {
    this.sessionId = id;
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping', payload: { timestamp: Date.now() } });
    }, HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private attemptReconnect(token: string) {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
    const delay = RECONNECT_DELAYS[this.reconnectAttempts] ?? RECONNECT_DELAYS[RECONNECT_DELAYS.length - 1];
    this.setStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(token);
    }, delay);
  }
}

export const wsManager = new WebSocketManager();
export default wsManager;
